import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, Zap, KeyRound, ShieldCheck, Euro, MapPin } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL, HERO_IMG } from "@/lib/site";
import { EASE } from "@/components/Reveal";

const LINES = [
  { text: "Guasto elettrico,", highlight: false },
  { text: "porta bloccata", highlight: true },
  { text: "o cancello fermo?", highlight: false },
];

const PILLS = [
  { icon: Zap, label: "Arrivo in 20-30 min" },
  { icon: KeyRound, label: "Zero danni alla porta" },
  { icon: ShieldCheck, label: "Prezzo chiaro prima di iniziare" },
  { icon: Euro, label: "Tecnici qualificati" },
];

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.14, delayChildren: 0.25 } },
};

const lineWrap = {
  hidden: {},
  show: { transition: { staggerChildren: 0.02 } },
};

const lineInner = {
  hidden: { y: "115%", rotate: 2 },
  show: { y: "0%", rotate: 0, transition: { duration: 0.95, ease: EASE } },
};

const fade = {
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: EASE } },
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -50]);

  return (
    <section ref={ref} id="top" className="relative overflow-hidden pb-16 pt-28 sm:pt-32 lg:pb-24 lg:pt-40">
      <div
        aria-hidden="true"
        className="absolute -top-40 left-1/2 h-[560px] w-[900px] -translate-x-1/2 animate-glow-breathe rounded-full bg-volt/10 blur-[140px]"
      />

      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-[1.05fr_0.95fr] lg:gap-10">
        <motion.div style={{ y: yText }} variants={container} initial="hidden" animate="show">
          <motion.div variants={fade}>
            <span className="inline-flex items-center gap-2.5 rounded-full border border-volt/30 bg-volt/10 px-4 py-2">
              <span className="h-2 w-2 animate-pulse-dot rounded-full bg-volt" />
              <span className="font-mono text-[11px] font-medium uppercase tracking-[0.24em] text-volt">
                24/7 · Pronto intervento · Roma e Provincia
              </span>
            </span>
          </motion.div>

          <h1 className="mt-7 font-display text-4xl font-black leading-[1.04] tracking-tight text-black sm:text-5xl lg:text-[4.2rem]">
            {LINES.map((line) => (
              <span key={line.text} className="block overflow-hidden pb-1">
                <motion.span
                  variants={lineWrap}
                  className="block"
                  style={{ transformOrigin: "left bottom" }}
                >
                  <motion.span variants={lineInner} className="block">
                    <span className={line.highlight ? "text-volt" : "text-black"}>
                      {line.text}
                    </span>
                  </motion.span>
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            variants={fade}
            className="mt-6 max-w-xl text-balance text-base font-medium text-slate-300 sm:text-lg"
          >
            Tecnico specializzato sul posto in 20-30 minuti a Roma e Provincia.
            Nessun costo nascosto: il prezzo è chiaro prima di iniziare.
          </motion.p>

          <motion.div variants={fade} className="mt-9 flex flex-col gap-3.5 sm:flex-row sm:items-center">
            <a
              href={PHONE_TEL}
              data-testid="hero-call-button"
              className="group relative inline-flex h-16 items-center justify-center gap-3 overflow-hidden rounded-full bg-volt px-8 font-display text-lg font-extrabold text-night shadow-[0_8px_40px_rgba(255,195,0,0.4)] transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_8px_56px_rgba(255,195,0,0.6)] active:scale-95"
            >
              <span
                aria-hidden="true"
                className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/40 to-transparent transition-transform duration-700 group-hover:translate-x-full"
              />
              <Phone size={22} strokeWidth={2.5} className="relative transition-transform duration-300 group-hover:rotate-12" />
              <span className="relative">CHIAMA ORA</span>
              <span className="relative font-mono text-xs font-semibold opacity-70">{PHONE_DISPLAY}</span>
            </a>
          </motion.div>

          <motion.ul variants={fade} className="mt-9 flex flex-wrap gap-2.5">
            {PILLS.map(({ icon: Icon, label }) => (
              <li
                key={label}
                className="flex items-center gap-2 rounded-full border border-line bg-ink/60 px-3.5 py-2 text-xs font-medium text-slate-300"
              >
                <Icon size={14} className="text-volt" strokeWidth={2.4} />
                {label}
              </li>
            ))}
          </motion.ul>
        </motion.div>

        <motion.div
          style={{ y: yImg }}
          initial={{ opacity: 0, scale: 0.94, y: 40 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 0.45, ease: EASE }}
          className="relative mx-auto w-full max-w-[560px]"
        >
          <div className="relative overflow-hidden rounded-3xl border border-line shadow-[0_40px_120px_rgba(0,0,0,0.6)]">
            <img
              src={HERO_IMG}
              alt="Roma di notte con le luci del traffico davanti al Colosseo"
              className="h-[380px] w-full object-cover sm:h-[480px] lg:h-[560px]"
              loading="eager"
            />
            <div
              aria-hidden="true"
              className="absolute inset-0 bg-gradient-to-t from-night via-night/20 to-transparent"
            />
            <div
              aria-hidden="true"
              className="absolute inset-0"
              style={{
                background:
                  "radial-gradient(600px 300px at 70% 20%, rgba(255,195,0,0.18), transparent 60%)",
              }}
            />
          </div>

          <div className="glass absolute -bottom-5 left-4 flex items-center gap-3.5 rounded-2xl border border-line px-5 py-4 shadow-2xl sm:left-6">
            <span className="relative flex h-3 w-3">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
              <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-400" />
            </span>
            <div>
              <p className="font-display text-sm font-bold text-white">Unità mobile in zona</p>
              <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-emerald-400">
                Tempo stimato · 22 min
              </p>
            </div>
          </div>

          <div className="absolute -top-9 right-3 hidden h-28 w-28 sm:block lg:-right-6">
            <div className="absolute inset-0 animate-spin-slow">
              <svg viewBox="0 0 100 100" className="h-full w-full">
                <defs>
                  <path id="circlePath" d="M 50,50 m -38,0 a 38,38 0 1,1 76,0 a 38,38 0 1,1 -76,0" />
                </defs>
                <text className="fill-volt font-mono text-[10px] uppercase" style={{ letterSpacing: "2.6px" }}>
                  <textPath href="#circlePath">RM · Pronto Intervento H24 · </textPath>
                </text>
              </svg>
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="flex h-11 w-11 items-center justify-center rounded-full border border-volt/40 bg-night">
                <MapPin size={17} className="text-volt" />
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
